// Canonicalisation — the comparison surface both sides measure themselves by.
//
// Pure functions, no SDK, no I/O, and deliberately NO HASHING: the extension
// hashes with `node:crypto` and the Durable Object with `crypto.subtle` (the
// Worker has no `nodejs_compat`), so the thing both sides must agree on is the
// canonical STRING, not the hash. Callers wrap these with their own digest
// primitive (`src/sync/digest.ts`, `workers/src/jam/digest.ts`).
//
// Used for one job in v3: telling a client WHETHER, and on which lane, its Live
// disagrees with the room — the input to a targeted repair.
//
// The comparison surface is deliberately exactly what sync carries and what
// survives the wire round-trip: tempo; per track name/mute/solo; per clip
// startTime/duration/name/muted/color (plus the slot for a Session View
// clip). It EXCLUDES warp/marker/filePath fields
// (region-rendered receiver copies legitimately differ there), cue points
// (receivers can only rename existing ones, never create — counts can differ
// forever), track ARM (record-arm is a per-musician stance, local-only —
// digesting it would read every armed track as permanent divergence and churn
// the 30s repair; 2026-08-25), and mixer VOLUME/PAN (synced, but the
// background poller cannot read the mixer — see CanonicalTrackState.volume —
// so a snapshot has no comparable value to digest). Numbers are rounded to
// ASSERT_EPS precision so float jitter never looks like divergence.


import type { WireNote } from "../midi/notes.js";
import type { ClipKind } from "./protocol.js";

/** Kind of a lane. Absent ⇒ "audio" (pre-MIDI rooms and snapshots). */
export type TrackKind = "audio" | "midi";

/** Precision for digest rounding AND clip startTime matching. */
export const ASSERT_EPS = 1e-4;

/** Round to 4 decimals (the digest/matching precision). */
export function r4(x: number): number {
  return Math.round(x * 1e4) / 1e4;
}

// ---------------------------------------------------------------------------
// Structural input types
//
// Narrow shapes covering exactly the fields canonicalisation reads. The extension's `Snapshot` (src/inspect/snapshot.ts) and the DO's
// canonical rows both satisfy these structurally, so neither side has to own
// the other's model — which is what lets this module live in shared/.
// ---------------------------------------------------------------------------

export interface CanonicalClip {
  name: string;
  startTime: number;
  duration: number;
  startMarker: number;
  endMarker: number;
  looping: boolean;
  loopStart: number;
  loopEnd: number;
  color: number;
  muted: boolean;
  filePath: string;
  warping: boolean;
  warpMode: { value: number };
  /** Absent ⇒ "audio". */
  clipKind?: ClipKind;
  /** MIDI only — enters the digest surface (a note edit is a content change
   * with identical geometry). */
  notesDigest?: string;
  /** MIDI only — the notes themselves, for the compiler to put on the wire.
   * Never digested directly (the digest stands in for them). */
  notes?: WireNote[];
  /** Session View clip-slot index. Absent ⇒ arrangement clip at startTime.
   * See protocol.ts ClipData.slot. */
  slot?: number;
  /** MIDI session clips: Live's Loop switch (protocol.ts ClipData.slotLooping). */
  slotLooping?: boolean;
}

/** A session clip sits in a clip slot; an arrangement clip sits on the timeline. */
export function isSessionClip(c: { slot?: number }): boolean {
  return c.slot !== undefined && c.slot !== null;
}

/**
 * THE per-lane identity of a clip: its slot for a session clip, its rounded
 * startTime for an arrangement clip. Arrangement clips can't overlap and a
 * slot holds one clip, so within one track this key is unique — it is what
 * bindings, the compiler's diff and the projection's handover all match on.
 * The two families never collide: "s<n>" is not a number.
 */
export function clipPlaceKey(c: { startTime: number; slot?: number }): string {
  return isSessionClip(c) ? `s${c.slot}` : String(r4(c.startTime));
}

/** Same place within a lane: same slot, or startTimes within ASSERT_EPS. */
export function samePlace(
  a: { startTime: number; slot?: number },
  b: { startTime: number; slot?: number },
): boolean {
  if (isSessionClip(a) || isSessionClip(b)) return isSessionClip(a) && isSessionClip(b) && a.slot === b.slot;
  return Math.abs(a.startTime - b.startTime) <= ASSERT_EPS;
}

/** Deterministic lane order: arrangement clips by startTime, then session
 * clips by slot. Audio-only lanes sort exactly as they always did. */
export function compareClipPlace(
  a: { startTime: number; slot?: number },
  b: { startTime: number; slot?: number },
): number {
  const sa = isSessionClip(a);
  const sb = isSessionClip(b);
  if (sa !== sb) return sa ? 1 : -1;
  if (sa && sb) return (a.slot as number) - (b.slot as number);
  return a.startTime - b.startTime;
}

/** A short human label for logs: "@12.5" or "[slot 3]". */
export function placeLabel(c: { startTime: number; slot?: number }): string {
  return isSessionClip(c) ? `[slot ${c.slot}]` : `@${r4(c.startTime)}`;
}

export interface CanonicalTrack {
  name: string;
  mute: boolean;
  solo: boolean;
  arm: boolean;
  /**
   * EVERY synced clip on the lane: arrangement clips (no `slot`) and Session
   * View clips (`slot` set), in one list, because the engine matches clips by
   * `clipPlaceKey` and never by array position. The name predates session
   * sync; a Snapshot keeps the two families apart (`arrangementClips` /
   * `sessionClips`) and snapshot-set.ts merges them onto this surface.
   */
  arrangementClips: CanonicalClip[];
  /** Absent ⇒ "audio". */
  trackKind?: TrackKind;
}

export interface CanonicalSet {
  /** `cuePoints` is present on a Live snapshot's song and absent on a
   * room's canonical set: locators are compared against the ROOM's list
   * (compile-intents), never digested. */
  song: { tempo: number; cuePoints?: Array<{ name: string; time: number }> };
  audioTracks: CanonicalTrack[];
  /**
   * MIDI lanes, kept in a PARALLEL collection so nothing that indexes
   * `audioTracks` ever moves (persisted bindings, seed join keys, fixtures).
   * The engine's single index space is `lanesOf(set)`: audio lanes first at
   * their unchanged indices, MIDI lanes after. Absent on everything that
   * predates MIDI sync.
   */
  midiTracks?: CanonicalTrack[];
}

/** THE coordinate system every index in the engine lives in: audio lanes at
 * [0, audioCount), MIDI lanes after. Mirrors LiveScope.tracksOf. */
export function lanesOf(set: CanonicalSet): CanonicalTrack[] {
  return set.midiTracks && set.midiTracks.length > 0
    ? [...set.audioTracks, ...set.midiTracks]
    : set.audioTracks;
}

/** The per-clip digest tuple. MIDI clips append their notes digest; audio
 * clips keep the exact 5-tuple they always had, so audio-only rooms and
 * snapshots digest byte-identically to before MIDI sync. */
function clipTuple(c: CanonicalClip): unknown[] {
  const t: unknown[] = [r4(c.startTime), r4(c.duration), c.name, c.muted, Math.round(c.color)];
  if (c.clipKind === "midi") t.push(c.notesDigest ?? "");
  // A session clip's slot is its position; arrangement clips (and every
  // pre-session room) keep their tuple byte-identical.
  if (isSessionClip(c)) t.push(`slot:${c.slot}`);
  return t;
}

/**
 * Per-name occurrence keys: ["Drums","Drums","Bass"] → distinct keys for the
 * 1st and 2nd "Drums", so duplicate track names still resolve/compare
 * deterministically. Shared by canonicalisation (order-insensitive sort), lane
 * keying for repair, and the room's lane→id lookup.
 */
export function occurrenceKeys(names: string[]): string[] {
  const seen = new Map<string, number>();
  return names.map((n) => {
    const k = seen.get(n) ?? 0;
    seen.set(n, k + 1);
    return `${n} ${k}`;
  });
}

/** Deterministic text form of ONE track's comparison surface. */
export function canonicalLane(t: CanonicalTrack): string {
  return JSON.stringify([
    t.name,
    t.mute,
    t.solo,
    [...t.arrangementClips].sort(compareClipPlace).map(clipTuple),
  ]);
}

/**
 * Per-lane canonical strings, keyed by name-occurrence. Lets a divergence check
 * localise the disagreement instead of only knowing the whole Set differs —
 * the input to the targeted repair that replaces the leader's 30s fullState.
 */
export function canonicalLanes(set: CanonicalSet): Array<{ key: string; canonical: string }> {
  const lanes = lanesOf(set);
  const keys = occurrenceKeys(lanes.map((t) => t.name));
  return lanes
    .map((t, i) => ({ key: keys[i]!, canonical: canonicalLane(t) }))
    .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
}

/** Deterministic text form of the comparison surface. Tracks are sorted by
 * name-key so lane ORDER never counts as divergence: two peers can legitimately
 * hold the same lanes in different orders (v2's reconcile appended and never
 * reordered, and v3's ords are display-only), and a comparison that treated that
 * as a difference would report divergence forever without anything being wrong. */
export function canonicalState(set: CanonicalSet): string {
  const lanes = lanesOf(set);
  const keys = occurrenceKeys(lanes.map((t) => t.name));
  const tracks = lanes
    .map((t, i) => ({ key: keys[i]!, t }))
    .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  return JSON.stringify([
    r4(set.song.tempo),
    tracks.map(({ t }) => [
      t.name,
      t.mute,
      t.solo,
      [...t.arrangementClips].sort(compareClipPlace).map(clipTuple),
    ]),
  ]);
}
